import { Component, ChangeDetectorRef, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  ApiEvent,
  EventService,
  EventAttendeesResponse,
  EventResponse,
  eventResponseOf,
} from '../../../services/event.service';
import { UserService } from '../../../services/user.service';
import { EventBuilder } from '../event-builder/event-builder';

export interface EventDetailData {
  event: ApiEvent;
  viewAsCounselor?: boolean;
}

@Component({
  selector: 'app-event-detail',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, DatePipe],
  templateUrl: './event-detail.html',
  styleUrl: './event-detail.css',
})
/** Dialog component that displays the full details of a single event. */
export class EventDetail implements OnInit {
  private data = inject<EventDetailData>(MAT_DIALOG_DATA);
  event = this.data.event;
  viewAsCounselor = !!this.data.viewAsCounselor;
  private ref = inject(MatDialogRef<EventDetail>);
  private dialog = inject(MatDialog);
  private eventService = inject(EventService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);

  /** Closes this dialog. */
  close(): void {
    this.ref.close();
  }

  /** Opens the EventBuilder dialog in edit mode and forwards its result on close. */
  openEdit(): void {
    const ref = this.dialog.open(EventBuilder, {
      width: '560px',
      maxWidth: '95vw',
      data: this.event,
    });
    ref.afterClosed().subscribe((result) => {
      this.ref.close(result);
    });
  }

  /**
   * Formats a start and optional end time into a readable label.
   * @param start - Start time in "HH:MM:SS" format
   * @param end - Optional end time in "HH:MM:SS" format
   * @returns String like "2:30 PM" or "2:30 PM - 3:00 PM"
   */
  timeLabel(start: string, end?: string | null): string {
    const fmt = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const hour = h % 12 || 12;
      const min = m.toString().padStart(2, '0');
      return `${hour}:${min} ${period}`;
    };
    return end ? `${fmt(start)} - ${fmt(end)}` : fmt(start);
  }

  /** True if the current user is a counselor (admin). */
  get isCounselor(): boolean {
    return this.viewAsCounselor && this.userService.currentUser?.userType === 'faculty';
  }

  /** True if the current user created this event. */
  get isOwner(): boolean {
    return this.event.user_id === this.userService.currentUser?.id;
  }

  /** True if the current user is a student. */
  get isStudent(): boolean {
    return !this.viewAsCounselor || this.userService.currentUser?.userType === 'student';
  }

  response: EventResponse | null = eventResponseOf(this.event);

  /** Records the current user's response to this event. */
  respond(status: EventResponse): void {
    if (this.response === status) return;
    this.eventService.setEventResponse(this.event.id, status).subscribe(() => {
      this.response = status;
      this.cdr.detectChanges();
    });
  }

  attendees: EventAttendeesResponse | null = null;

  /** Fetches attendees if the user is the counselor-owner of a shared event. */
  ngOnInit(): void {
    if (this.isCounselor && this.isOwner && this.event.is_shared) {
      this.eventService.getEventAttendees(this.event.id).subscribe((data) => {
        this.attendees = data;
        this.cdr.detectChanges();
      });
    }
  }
}
